/**
 * An invite's cryptography on the writer's device (decision 75; principle 10; part 38b log, principles DI1–DI4): the
 * share's owner secret, each invite's claim secret and person key, made at random and kept only in the share record; the
 * owner proof, the claim proof and the IDs derived from them (part 38a's `shareIdFor`, `inviteIdFor`), which are all the
 * relay is sent of them; a copy sealed under a fresh copy key per version, and that key wrapped under each holder's
 * person key, each authenticated with what it belongs to, so the relay can't move a copy between items, shares or
 * versions, or a wrap between invites. The invitee's device (part 38c) opens them with the same functions. WebCrypto is
 * injected.
 */

/** A share's owner secret and an invite's claim secret: 128 random bits each (part 38a, IN10). */
export const INVITE_SECRET_BYTES = 16;

/** A person key and a copy key: AES-256 (DI1). */
export const INVITE_KEY_BYTES = 32;

/** An owner or claim proof: a SHA-256 (part 38a's `LINK_PROOF_BYTES`). */
export const INVITE_PROOF_BYTES = 32;

/** A share's and an invite's ID: 16 bytes (part 38a's `SHARE_ID_BYTES`). */
export const INVITE_ID_BYTES = 16;

/** A copy's version: 16 bytes, compared as bytes (part 38a's `COPY_VERSION_BYTES`, IA15). */
export const COPY_VERSION_BYTES = 16;

/** AES-GCM's IV, which leads every sealed copy and wrap. */
export const IV_BYTES = 12;

/** AES-GCM's tag. */
export const TAG_BYTES = 16;

/** What a wrap of a copy key takes: its IV, the key and the tag (well within part 38a's `MAX_WRAP_BYTES`). */
export const COPY_WRAP_BYTES = IV_BYTES + INVITE_KEY_BYTES + TAG_BYTES;

/** What sealing adds to a copy's plaintext: its IV and its tag. */
export const COPY_SEAL_BYTES = IV_BYTES + TAG_BYTES;

const encoder = new TextEncoder();
const OWNER_DOMAIN = encoder.encode('cockatrice/share/owner/v1');
const CLAIM_DOMAIN = encoder.encode('cockatrice/invite/claim/v1');
const SHARE_ID_DOMAIN = encoder.encode('cockatrice/share/id/v1');
const INVITE_ID_DOMAIN = encoder.encode('cockatrice/invite/id/v1');
const COPY_DOMAIN = encoder.encode('cockatrice/invite/copy/v1');
const WRAP_DOMAIN = encoder.encode('cockatrice/invite/copy-key/v2');

/** `parts` joined, in order (the invite notes' places use it too). */
export function joined(...parts: readonly Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(parts.reduce((length, part) => length + part.length, 0));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

/** `text` as UTF-8, its length first (two bytes, big-endian), so two fields can never run into each other. */
export function field(text: string): Uint8Array<ArrayBuffer> {
  const bytes = encoder.encode(text);
  const out = new Uint8Array(2 + bytes.length);
  new DataView(out.buffer).setUint16(0, bytes.length);
  out.set(bytes, 2);
  return out;
}

/** SHA-256 of `bytes`. */
export async function sha256(subtle: SubtleCrypto, bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await subtle.digest('SHA-256', bytes));
}

/** Throws unless `bytes` has `length` bytes: a caller's mistake, never input from outside (that is checked at its boundary). */
export function lengthOf(bytes: Uint8Array, length: number, what: string): void {
  if (bytes.length !== length) throw new RangeError(`${what} must be ${String(length)} bytes`);
}

/** DI2: the owner proof of a share's owner secret `secret`: SHA-256 of its domain and the secret. */
export async function shareOwnerProof(subtle: SubtleCrypto, secret: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(secret, INVITE_SECRET_BYTES, 'an owner secret');
  return sha256(subtle, joined(OWNER_DOMAIN, secret));
}

/** DI2: the claim proof of an invite's claim secret `secret`: SHA-256 of its domain and the secret. */
export async function claimProof(subtle: SubtleCrypto, secret: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(secret, INVITE_SECRET_BYTES, 'a claim secret');
  return sha256(subtle, joined(CLAIM_DOMAIN, secret));
}

/** Part 38a's IP2: the only ID a share of owner proof `proof` may have: the first 16 bytes of SHA-256(domain ‖ proof). */
export async function shareIdOf(subtle: SubtleCrypto, proof: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(proof, INVITE_PROOF_BYTES, 'an owner proof');
  return (await sha256(subtle, joined(SHARE_ID_DOMAIN, proof))).slice(0, INVITE_ID_BYTES);
}

/** Part 38a's IP2: the only ID an invite of claim proof `proof` may have. */
export async function inviteIdOf(subtle: SubtleCrypto, proof: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(proof, INVITE_PROOF_BYTES, 'a claim proof');
  return (await sha256(subtle, joined(INVITE_ID_DOMAIN, proof))).slice(0, INVITE_ID_BYTES);
}

/** An AES-GCM key of `raw`, not extractable. */
export function aesKey(subtle: SubtleCrypto, raw: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  return subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
}

/** DI4: what a copy is authenticated with: its domain, the share, the item and the version. */
function copyData(share: Uint8Array, item: string, version: Uint8Array): Uint8Array<ArrayBuffer> {
  return joined(COPY_DOMAIN, share, field(item), version);
}

/**
 * DI4: what a copy key's wrap is authenticated with: its domain, the share, the invite, the item and the version; and
 * (fix-next 85, DI4′: v2) SHA-256 of the sealed copy, so a wrap under one holder's person key vouches for those bytes
 * alone. Every holder of an item shares its copy key; without the hash any of them could seal other text at its place
 * and a relay serve it to the rest as the writer's.
 */
async function wrapData(subtle: SubtleCrypto, share: Uint8Array, invite: Uint8Array, item: string, version: Uint8Array, sealed: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  return joined(WRAP_DOMAIN, share, invite, field(item), version, await sha256(subtle, sealed));
}

/** Where a copy belongs: its share's ID, its item (a chapter's ID or an invite's manifest) and its version. */
export interface CopyPlace {
  readonly share: Uint8Array;
  readonly item: string;
  readonly version: Uint8Array;
}

function placeOf(place: CopyPlace): void {
  lengthOf(place.share, INVITE_ID_BYTES, 'a share ID');
  lengthOf(place.version, COPY_VERSION_BYTES, 'a version');
}

/** DI4: `plain` sealed as the copy at `place` under copy key `key`, with the fresh IV `iv`: the IV, then the ciphertext. */
export async function sealCopy(subtle: SubtleCrypto, key: Uint8Array<ArrayBuffer>, place: CopyPlace, plain: Uint8Array<ArrayBuffer>, iv: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(key, INVITE_KEY_BYTES, 'a copy key');
  lengthOf(iv, IV_BYTES, 'an IV');
  placeOf(place);
  return joined(iv, new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv, additionalData: copyData(place.share, place.item, place.version) }, await aesKey(subtle, key), plain)));
}

/** DI4: the copy at `place` opened with copy key `key`; null when it doesn't open (another place's, altered, short). */
export async function openCopy(subtle: SubtleCrypto, key: Uint8Array<ArrayBuffer>, place: CopyPlace, bytes: Uint8Array): Promise<Uint8Array<ArrayBuffer> | null> {
  if (key.length !== INVITE_KEY_BYTES || place.share.length !== INVITE_ID_BYTES || place.version.length !== COPY_VERSION_BYTES || bytes.length < COPY_SEAL_BYTES) return null;
  try {
    const data = copyData(place.share, place.item, place.version);
    return new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv: bytes.slice(0, IV_BYTES), additionalData: data }, await aesKey(subtle, key), bytes.slice(IV_BYTES)));
  } catch {
    return null;
  }
}

/**
 * DI4: copy key `key` of the copy at `place` wrapped for invite `invite` under its person key `person`, with the fresh IV
 * `iv`; DI4′: bound to `sealed`, the copy's bytes as {@link sealCopy} made them.
 */
export async function wrapCopyKey(
  subtle: SubtleCrypto,
  person: Uint8Array<ArrayBuffer>,
  invite: Uint8Array,
  place: CopyPlace,
  key: Uint8Array<ArrayBuffer>,
  iv: Uint8Array<ArrayBuffer>,
  sealed: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(person, INVITE_KEY_BYTES, 'a person key');
  lengthOf(invite, INVITE_ID_BYTES, 'an invite ID');
  lengthOf(key, INVITE_KEY_BYTES, 'a copy key');
  lengthOf(iv, IV_BYTES, 'an IV');
  placeOf(place);
  const data = await wrapData(subtle, place.share, invite, place.item, place.version, sealed);
  return joined(iv, new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv, additionalData: data }, await aesKey(subtle, person), key)));
}

/**
 * DI4: the copy key a wrap for invite `invite` at `place` holds, opened with person key `person`; null when it doesn't
 * open. DI4′: only for the sealed copy `sealed` it was made for (another's, a co-holder's re-sealing, opens nothing).
 */
export async function unwrapCopyKey(subtle: SubtleCrypto, person: Uint8Array<ArrayBuffer>, invite: Uint8Array, place: CopyPlace, wrap: Uint8Array, sealed: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer> | null> {
  if (person.length !== INVITE_KEY_BYTES || invite.length !== INVITE_ID_BYTES || place.share.length !== INVITE_ID_BYTES || place.version.length !== COPY_VERSION_BYTES || wrap.length !== COPY_WRAP_BYTES) {
    return null;
  }
  try {
    const data = await wrapData(subtle, place.share, invite, place.item, place.version, sealed);
    // A wrap of exactly COPY_WRAP_BYTES opens to exactly a key's bytes.
    return new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv: wrap.slice(0, IV_BYTES), additionalData: data }, await aesKey(subtle, person), wrap.slice(IV_BYTES)));
  } catch {
    return null;
  }
}
